import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { LoadingState } from '@/components/Spinner';
import { SessionMeetingSummary } from '@/components/review/SessionMeetingSummary';
import { ReviewTranscriptPanel } from '@/features/review/ReviewTranscriptPanel';
import type { ReviewSessionDetailResponse } from '@/types/review';
import { formatSessionDay } from '@/utils/meetingType';

export function ShadowValidationSessionDetailPage() {
  const { sessionId } = useParams();
  const [detail, setDetail] = useState<ReviewSessionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<ReviewSessionDetailResponse>(`/shadow-validation/sessions/${encodeURIComponent(sessionId)}`)
      .then((resp) => {
        if (cancelled) return;
        setDetail(resp.data);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('確認用セッション詳細を取得できませんでした');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const summary = detail?.session;
  const detailForPanel = useMemo(() => {
    if (!detail) return null;
    const panelDisplayName = [detail.session.client_name, detail.session.coach_name]
      .filter(Boolean)
      .join(' / ');
    return {
      ...detail,
      session: {
        ...detail.session,
        display_name: panelDisplayName || '確認用セッション',
        client_name: null,
      },
      additional_feedbacks: [],
      session_comments: [],
      my_overall_comment_body: null,
    };
  }, [detail]);

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-[1920px] flex-col">
      <div className="shrink-0">
        <div className="mb-3">
          <Link to="/shadow-validation/sessions" className="text-sm text-blue-600 hover:underline">
            ← 確認用セッション一覧
          </Link>
        </div>
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start">
          <div className="min-w-0 shrink-0 lg:max-w-md">
            <h1 className="text-xl font-semibold text-gray-900">フィードバック</h1>
            {summary && (
              <p className="mt-1 text-sm text-gray-500">
                {summary.client_name ?? '-'} / {summary.coach_name ?? '-'} -{' '}
                {formatSessionDay(summary.day, summary.day1_variant)}
              </p>
            )}
          </div>
          <SessionMeetingSummary summary={summary?.meeting_summary} className="w-full lg:flex-1" />
        </div>
      </div>

      <div className="mt-4 flex h-[calc(100vh-13rem)] min-h-[34rem] min-w-0 flex-col">
        {loading ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12">
            <LoadingState message="確認用フィードバックを読み込み中..." />
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 text-sm text-red-700">
            {error}
          </div>
        ) : detailForPanel ? (
          <ReviewTranscriptPanel
            detail={detailForPanel}
            canEditReviews={false}
            showAdditionalFeedback={false}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white p-12 text-sm text-gray-400">
            確認用フィードバックがありません
          </div>
        )}
      </div>
    </div>
  );
}
